<script setup lang="ts">
import { computed, nextTick, onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatApiError } from '@yuksalish/api-client'
import { useKioskApi } from '../composables/useKioskApi'
import { useOfflineQueue, isNetworkError } from '../composables/useOfflineQueue'
import { nowApiDateTime } from '@yuksalish/ui'
import { ArrowLeft, CircleCheck, CircleX, Moon, Pause, Play, RotateCcw, Sun } from 'lucide-vue-next'
import type { EquipmentItem, KioskEmployee, KioskLot, KioskOrder, KioskStage, PieceRate, WorkerSession } from '../types/kiosk'

const { t } = useI18n()
const WORKER_KEY = 'yuksalish.kiosk.worker_id'

function readStoredWorker(): number | null {
  try {
    const value = localStorage.getItem(WORKER_KEY)
    return value ? Number(value) : null
  } catch {
    return null
  }
}

function storeWorker(id: number | null): void {
  try {
    if (id === null) localStorage.removeItem(WORKER_KEY)
    else localStorage.setItem(WORKER_KEY, String(id))
  } catch {
    // worker still applies for this session
  }
}
const {
  listActiveOrders,
  listStages,
  listLots,
  recordProgress,
  closeStage,
  listDowntimeReasons,
  listEquipment,
  openDowntime,
  listProductionEmployees,
  workerLogin,
  startSession,
  pauseSession,
  resumeSession,
  finishSession,
  fetchPieceRates,
} = useKioskApi()

const PENDING_KEY = 'yuksalish.kiosk.pending_actions'

const {
  pendingCount,
  isOnline,
  flushing: syncing,
  enqueue,
  remove: removeQueued,
  flush: flushQueue,
} = useOfflineQueue({
  storageKey: PENDING_KEY,
  isRetryable: isNetworkError,
  execute: async (action) => {
    if (action.type === 'start') {
      const session = await startSession(action.payload as Parameters<typeof startSession>[0])
      activeSession.value = session
      return
    }
    if (action.type === 'pause') {
      const session = await pauseSession(
        Number(action.payload.sessionId),
        {
          paused_at: String(action.payload.paused_at),
          client_event_id: action.clientEventId,
        },
      )
      activeSession.value = session
      return
    }
    if (action.type === 'resume') {
      const session = await resumeSession(
        Number(action.payload.sessionId),
        {
          resumed_at: String(action.payload.resumed_at),
          client_event_id: action.clientEventId,
        },
      )
      activeSession.value = session
      return
    }
    if (action.type === 'finish') {
      const session = await finishSession(
        Number(action.payload.sessionId),
        {
          finished_at: String(action.payload.finished_at),
          completed_delta: Number(action.payload.completed_delta) || 0,
          rejected_delta: Number(action.payload.rejected_delta) || 0,
          rework_delta: Number(action.payload.rework_delta) || 0,
          client_event_id: action.clientEventId,
        },
      )
      activeSession.value = session
    }
  },
})

const pieceRates = ref<PieceRate[]>([])
const estimatedEarnings = ref<number | null>(null)
const estimatedCurrency = ref('')
/** Active shift for rate lookup + session start. Defaults from worker-login. */
const currentShift = ref<'day' | 'night'>('day')

const workers = ref<KioskEmployee[]>([])
const orders = ref<KioskOrder[]>([])
const stages = ref<KioskStage[]>([])
const lots = ref<KioskLot[]>([])

const selectedWorkerId = ref<number | null>(readStoredWorker())
const selectedOrderId = ref<number | null>(null)
const selectedLot = ref<KioskLot | null>(null)
const activeSession = ref<WorkerSession | null>(null)

const scan = ref('')
const scanError = ref('')
const output = reactive({ completed: 0, rejected: 0, rework: 0 })
const actionError = ref('')
const actionMessage = ref('')
const submitting = ref(false)

const showWorkerPicker = ref(false)
const loginMode = ref<'pick' | 'card'>('pick')
const loginCard = ref('')
const loginPin = ref('')
const loginError = ref('')
const cardInput = ref<HTMLInputElement | null>(null)
const scanInput = ref<HTMLInputElement | null>(null)

const showDowntime = ref(false)
const downtimeReasons = ref<Array<{ code: string; name: string }>>([])
const equipment = ref<EquipmentItem[]>([])
const downtimeForm = reactive({ reason_code: '', notes: '', machine_id: null as number | null })

const loading = ref(true)
const error = ref('')

const selectedWorker = computed(() =>
  workers.value.find((w) => w.id === selectedWorkerId.value) ?? null,
)
const activeLots = computed(() => lots.value.filter((l) => l.status === 'active'))

function stageName(code: string): string {
  return stages.value.find((s) => s.stage_code === code)?.stage_name ?? code
}

function workerLabel(worker: KioskEmployee): string {
  return `${worker.first_name} ${worker.last_name}`.trim()
}

/** Localized downtime reason label, falling back to the backend name. */
function reasonLabel(code: string, fallback = ''): string {
  const key = `kiosk.session.reasons.${code}`
  const value = t(key)
  return value === key ? fallback || code : value
}

function pinAppend(digit: string) {
  if (loginPin.value.length < 12) loginPin.value += digit
}

function pinBackspace() {
  loginPin.value = loginPin.value.slice(0, -1)
}

function pinClear() {
  loginPin.value = ''
}

async function loadWorkers() {
  try {
    workers.value = await listProductionEmployees()
  } catch {
    workers.value = []
  }
}

async function loadEquipment() {
  try {
    equipment.value = await listEquipment()
  } catch {
    equipment.value = []
  }
}

/** Best-effort extraction of a color-variant label from the lot's
 *  `color_summary` payload (shape varies: string | array | object). */
function parseColorVariant(summary: unknown): string {
  if (summary == null) return ''
  if (typeof summary === 'string') return summary.trim()
  if (Array.isArray(summary)) {
    return summary
      .map((item) => {
        if (typeof item === 'string') return item
        if (item && typeof item === 'object') {
          const record = item as Record<string, unknown>
          return record.name ?? record.color_name ?? record.color ?? record.label ?? ''
        }
        return ''
      })
      .filter((value) => typeof value === 'string' && value.trim())
      .join(', ')
  }
  if (typeof summary === 'object') {
    const record = summary as Record<string, unknown>
    if (Array.isArray(record.colors)) return parseColorVariant(record.colors)
    if (Array.isArray(record.items)) return parseColorVariant(record.items)
    const direct = record.name ?? record.color_name ?? record.color ?? record.label
    if (typeof direct === 'string' && direct.trim()) return direct.trim()
  }
  return ''
}

/** Attaches order context (order code, product) + parsed color variant to a
 *  backend lot so every card on the universal board is self-contained. */
function enrichLot(lot: KioskLot, order: KioskOrder): KioskLot {
  return {
    ...lot,
    order_code: order.mo_code,
    product_name: order.product_name,
    color_variant: parseColorVariant(lot.color_summary) || undefined,
  }
}

/** Fetches lots + stages for every active order and flattens them into a
 *  single universal board. Keeps the selected lot object in sync. */
async function fetchBoard(): Promise<boolean> {
  if (!orders.value.length) {
    lots.value = []
    stages.value = []
    return true
  }
  try {
    const results = await Promise.all(
      orders.value.map(async (order) => {
        const [orderLots, orderStages] = await Promise.all([
          listLots(order.id),
          listStages(order.id),
        ])
        return { order, lots: orderLots, stages: orderStages }
      }),
    )
    lots.value = results.flatMap(({ order, lots: orderLots }) =>
      orderLots.map((lot) => enrichLot(lot, order)),
    )
    stages.value = results.flatMap(({ stages }) => stages)
    const current = selectedLot.value
    if (current) {
      const updated = lots.value.find((l) => l.id === current.id)
      if (updated) selectedLot.value = updated
    }
    return true
  } catch {
    return false
  }
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    orders.value = await listActiveOrders()
    const ok = await fetchBoard()
    if (!ok) error.value = t('kiosk.session.error')
    if (!selectedOrderId.value && orders.value.length > 0) {
      selectedOrderId.value = orders.value[0].id
    }
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

async function loadDowntimeReasons() {
  try {
    const reasons = await listDowntimeReasons()
    downtimeReasons.value = reasons
  } catch {
    downtimeReasons.value = []
  }
}

onMounted(() => {
  void loadWorkers()
  void loadDowntimeReasons()
  void loadEquipment()
  void load()
  void flushQueue()
})

function pickWorker() {
  showWorkerPicker.value = true
  loginMode.value = 'pick'
  loginCard.value = ''
  loginPin.value = ''
  loginError.value = ''
}

function switchLoginMode(mode: 'pick' | 'card') {
  loginMode.value = mode
  loginError.value = ''
  if (mode === 'card') {
    void nextTick(() => {
      cardInput.value?.focus()
    })
  }
}

function selectWorker(id: number) {
  selectedWorkerId.value = id
  activeSession.value = null
  storeWorker(id)
  showWorkerPicker.value = false
}

async function submitWorkerLogin() {
  loginError.value = ''
  if (!loginCard.value.trim() && !loginPin.value.trim()) {
    loginError.value = t('kiosk.session.selectWorker')
    return
  }
  submitting.value = true
  try {
    const response = await workerLogin({
      card_uid: loginCard.value.trim() || undefined,
      pin: loginPin.value.trim() || undefined,
    })
    selectedWorkerId.value = response.employee.id
    storeWorker(response.employee.id)
    currentShift.value = response.shift === 'night' ? 'night' : 'day'
    // ensure the logged-in worker is present in the picker list
    if (!workers.value.some((w) => w.id === response.employee.id)) {
      workers.value.unshift({
        id: response.employee.id,
        employee_code: response.employee.employee_code,
        first_name: response.employee.name,
        last_name: '',
      })
    }
    // Step 1 — swipe while a lot is selected binds the worker and starts the
    // session (non-destructive). Re-swipes are no-ops; an active session on a
    // different lot asks before switching.
    if (response.active_session) {
      const sameLot =
        !!selectedLot.value &&
        selectedLot.value.lot_number === response.active_session.lot_number
      if (sameLot) {
        activeSession.value = response.active_session
        actionMessage.value = t('kiosk.session.activeOnLot', {
          lot: response.active_session.lot_number,
        })
      } else if (window.confirm(t('kiosk.session.switchToActiveLot', { lot: response.active_session.lot_number }))) {
        activeSession.value = response.active_session
        await restoreActiveSessionContext(response.active_session)
        actionMessage.value = t('kiosk.session.activeOnLot', {
          lot: response.active_session.lot_number,
        })
      } else {
        actionMessage.value = `${t('kiosk.session.worker')}: ${response.employee.name}`
      }
    } else if (selectedLot.value) {
      await startSessionForWorker(response.employee.id, selectedLot.value)
    } else {
      actionMessage.value = `${t('kiosk.session.worker')}: ${response.employee.name}`
    }
    showWorkerPicker.value = false
  } catch (cause) {
    loginError.value = formatApiError(cause)
  } finally {
    submitting.value = false
  }
}

async function restoreActiveSessionContext(session: WorkerSession) {
  try {
    currentShift.value = session.shift === 'night' ? 'night' : 'day'
    if (!orders.value.some((o) => o.id === session.order_id)) {
      orders.value = await listActiveOrders()
      await fetchBoard()
    }
    selectedOrderId.value = session.order_id
    const match = lots.value.find(
      (l) => l.lot_number === session.lot_number && l.production_order_id === session.order_id,
    )
    if (match) {
      selectLot(match)
    }
  } catch {
    // keep current context if restoration fails
  }
}

function nowStamp(): string {
  return nowApiDateTime()
}

function nextClientEventId(action: string): string {
  return `kiosk-${selectedWorkerId.value ?? 0}-${selectedLot.value?.production_order_id ?? 0}-${action}-${Date.now()}`
}

/** Step 1 (bind): starts a worker session on the given lot + current stage.
 *  Idempotent via client_event_id; offline actions are queued. Shared by the
 *  Start button and the card-swipe auto-bind. */
async function startSessionForWorker(workerId: number, lot: KioskLot): Promise<boolean> {
  const clientEventId = nextClientEventId('start')
  const payload = {
    employee_id: workerId,
    order_id: lot.production_order_id,
    client_event_id: clientEventId,
    shift: currentShift.value,
    lot_number: lot.lot_number,
    stage_code: lot.current_stage_code,
    started_at: nowStamp(),
  }
  submitting.value = true
  try {
    const session = await startSession(payload)
    activeSession.value = session
    currentShift.value = session.shift === 'night' ? 'night' : 'day'
    actionMessage.value = t('kiosk.session.started')
    await loadPieceRates()
    return true
  } catch (cause) {
    if (!isOnline.value || isNetworkError(cause)) {
      enqueue('start', clientEventId, payload)
      actionMessage.value = t('kiosk.session.queued')
      return true
    }
    actionError.value = formatApiError(cause)
    return false
  } finally {
    submitting.value = false
  }
}

async function submitStartSession() {
  actionError.value = ''
  actionMessage.value = ''
  if (!selectedWorkerId.value) {
    actionError.value = t('kiosk.session.selectEmployee')
    return
  }
  if (!selectedLot.value) {
    actionError.value = t('kiosk.session.selectLotFirst')
    return
  }
  await startSessionForWorker(selectedWorkerId.value, selectedLot.value)
}

async function submitPause() {
  if (!activeSession.value) return
  const clientEventId = nextClientEventId('pause')
  const payload = { sessionId: activeSession.value.id, paused_at: nowStamp() }
  submitting.value = true
  try {
    activeSession.value = await pauseSession(activeSession.value.id, {
      paused_at: payload.paused_at,
      client_event_id: clientEventId,
    })
    actionMessage.value = t('kiosk.session.pausedMsg')
  } catch (cause) {
    if (!isOnline.value || isNetworkError(cause)) {
      enqueue('pause', clientEventId, payload)
      actionMessage.value = t('kiosk.session.queued')
    } else {
      actionError.value = formatApiError(cause)
    }
  } finally {
    submitting.value = false
  }
}

async function submitResume() {
  if (!activeSession.value) return
  const clientEventId = nextClientEventId('resume')
  const payload = { sessionId: activeSession.value.id, resumed_at: nowStamp() }
  submitting.value = true
  try {
    activeSession.value = await resumeSession(activeSession.value.id, {
      resumed_at: payload.resumed_at,
      client_event_id: clientEventId,
    })
    actionMessage.value = t('kiosk.session.resumedMsg')
  } catch (cause) {
    if (!isOnline.value || isNetworkError(cause)) {
      enqueue('resume', clientEventId, payload)
      actionMessage.value = t('kiosk.session.queued')
    } else {
      actionError.value = formatApiError(cause)
    }
  } finally {
    submitting.value = false
  }
}

async function submitFinish() {
  if (!activeSession.value) return
  const clientEventId = nextClientEventId('finish')
  const payload = {
    sessionId: activeSession.value.id,
    finished_at: nowStamp(),
    completed_delta: Number(output.completed) || 0,
    rejected_delta: Number(output.rejected) || 0,
    rework_delta: Number(output.rework) || 0,
  }
  submitting.value = true
  try {
    const finished = await finishSession(activeSession.value.id, {
      finished_at: payload.finished_at,
      completed_delta: payload.completed_delta,
      rejected_delta: payload.rejected_delta,
      rework_delta: payload.rework_delta,
      client_event_id: clientEventId,
    })
    activeSession.value = finished
    output.completed = 0
    output.rejected = 0
    output.rework = 0
    actionMessage.value = t('kiosk.session.finishedMsg')
    await refreshLots()
  } catch (cause) {
    if (!isOnline.value || isNetworkError(cause)) {
      enqueue('finish', clientEventId, payload)
      actionMessage.value = t('kiosk.session.queued')
    } else {
      actionError.value = formatApiError(cause)
    }
  } finally {
    submitting.value = false
  }
}

function sessionStatusLabel(session: WorkerSession): string {
  if (session.status === 'active') return t('kiosk.session.sessionActive')
  if (session.status === 'paused') return t('kiosk.session.sessionPaused')
  return t('kiosk.session.sessionFinished')
}

function elapsedMinutes(session: WorkerSession): number {
  if (!session.total_seconds) return 0
  return Math.round(session.total_seconds / 60)
}

function selectLot(lot: KioskLot) {
  selectedLot.value = lot
  scan.value = ''
  scanError.value = ''
  // Default Good to the lot's remaining on-hand quantity so the operator can
  // confirm the whole remaining lot at this stage without re-entering it.
  output.completed = Math.max(0, Number(lot.current_quantity) || 0)
  output.rejected = 0
  output.rework = 0
  actionError.value = ''
  actionMessage.value = ''
  pieceRates.value = []
  estimatedEarnings.value = null
  // Universal terminal: the lot card only opens after the worker is verified
  // (card swipe / PIN) and bound as the active terminal worker.
  if (!selectedWorkerId.value) {
    showWorkerPicker.value = true
    loginMode.value = 'card'
    loginCard.value = ''
    loginPin.value = ''
    loginError.value = ''
    void nextTick(() => {
      cardInput.value?.focus()
    })
    return
  }
  void loadPieceRates()
  void nextTick(() => {
    scanInput.value?.focus()
  })
}

function openScannedLot() {
  const value = scan.value.trim()
  scanError.value = ''
  if (!value) return

  // 1) Try to match a lot in the currently selected order first.
  const lotMatch = lots.value.find(
    (item) => item.lot_number.toLowerCase() === value.toLowerCase(),
  )
  if (lotMatch) {
    selectLot(lotMatch)
    scan.value = ''
    return
  }

  // 2) An order-code scan simply confirms the order — all its lots are
  //    already on the universal board.
  const orderMatch = orders.value.find(
    (o) => o.mo_code.toLowerCase() === value.toLowerCase(),
  )
  if (orderMatch) {
    scan.value = ''
    actionMessage.value = `${t('kiosk.session.order')}: ${orderMatch.mo_code}`
    return
  }

  scanError.value = t('kiosk.session.notFound')
}

function clearSelection() {
  selectedLot.value = null
  actionMessage.value = ''
  actionError.value = ''
}

/** Mirrors recorded deltas onto the active session so the Step 2 completion
 *  prompt can compare the bound worker's output against the lot's on-hand
 *  quantity for the current stage. */
function addSessionOutput(completed: number, rejected: number, rework: number): void {
  const session = activeSession.value
  if (!session) return
  activeSession.value = {
    ...session,
    completed_quantity: Number(session.completed_quantity || 0) + completed,
    rejected_quantity: Number(session.rejected_quantity || 0) + rejected,
    rework_quantity: Number(session.rework_quantity || 0) + rework,
  }
}

async function submitOutput() {
  actionError.value = ''
  actionMessage.value = ''
  if (!selectedWorkerId.value) {
    actionError.value = t('kiosk.session.selectEmployee')
    return
  }
  if (!selectedLot.value) return
  if (output.completed <= 0 && output.rejected <= 0 && output.rework <= 0) return

  const completedDelta = Number(output.completed) || 0
  const rejectedDelta = Number(output.rejected) || 0
  const reworkDelta = Number(output.rework) || 0

  submitting.value = true
  try {
    await recordProgress(
      selectedLot.value.production_order_id,
      selectedLot.value.lot_number,
      selectedLot.value.current_stage_code,
      {
        completed_delta: completedDelta,
        rejected_delta: rejectedDelta,
        rework_delta: reworkDelta,
        employee_id: selectedWorkerId.value,
      },
    )
    actionMessage.value = t('kiosk.session.progressRecorded', { lot: selectedLot.value.lot_number })
    addSessionOutput(completedDelta, rejectedDelta, reworkDelta)
    output.completed = 0
    output.rejected = 0
    output.rework = 0
    await refreshLots()
    recomputeEstimate()

    // Step 2 — when the bound worker's output reaches the lot's on-hand
    // quantity for the current stage, ask to close it (gated + confirmed).
    const lotQty = Number(selectedLot.value?.current_quantity) || 0
    const session = activeSession.value
    const sessionOutput =
      (Number(session?.completed_quantity) || 0) + (Number(session?.rejected_quantity) || 0)
    if (
      selectedLot.value &&
      session &&
      session.lot_number === selectedLot.value.lot_number &&
      session.status !== 'finished' &&
      lotQty >= 1 &&
      sessionOutput >= lotQty
    ) {
      if (window.confirm(t('kiosk.session.closeConfirm'))) {
        await doCloseStage()
      } else {
        actionMessage.value = t('kiosk.session.stageComplete')
      }
    }
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    submitting.value = false
  }
}

/** Step 2 (close): closes the current stage of the selected lot, recording
 *  the bound worker for the audit trail. Shared by the explicit Close button
 *  and the completion prompt so the confirmation is never asked twice. */
async function doCloseStage(): Promise<boolean> {
  if (!selectedWorkerId.value || !selectedLot.value) return false
  submitting.value = true
  try {
    await closeStage(
      selectedLot.value.production_order_id,
      selectedLot.value.lot_number,
      selectedLot.value.current_stage_code,
      { employee_id: selectedWorkerId.value },
    )
    actionMessage.value = t('kiosk.session.stageClosed', { lot: selectedLot.value.lot_number })
    selectedLot.value = null
    await refreshLots()
    return true
  } catch (cause) {
    actionError.value = formatApiError(cause)
    return false
  } finally {
    submitting.value = false
  }
}

async function submitClose() {
  actionError.value = ''
  actionMessage.value = ''
  if (!selectedWorkerId.value) {
    actionError.value = t('kiosk.session.selectEmployee')
    return
  }
  if (!selectedLot.value) return
  if (!window.confirm(t('kiosk.session.closeConfirm'))) return
  await doCloseStage()
}

async function refreshLots() {
  await fetchBoard()
}

async function loadPieceRates() {
  const stageCode = selectedLot.value?.current_stage_code
  if (!stageCode || !selectedWorkerId.value) return
  try {
    pieceRates.value = await fetchPieceRates({
      stage_code: stageCode,
      employee_id: selectedWorkerId.value,
      shift: currentShift.value,
    })
  } catch {
    pieceRates.value = []
  }
  recomputeEstimate()
}

function recomputeEstimate() {
  const stageCode = selectedLot.value?.current_stage_code
  if (!stageCode || !activeSession.value) {
    estimatedEarnings.value = null
    return
  }
  const completed = Number(activeSession.value.completed_quantity) || 0
  const rate = pieceRates.value.find(
    (r) => r.stage_code === stageCode && r.shift === currentShift.value,
  )
  if (!rate) {
    estimatedEarnings.value = null
    return
  }
  estimatedEarnings.value = completed * Number(rate.rate_per_pair) || 0
  estimatedCurrency.value = rate.currency_code || ''
}

const stageRate = computed(() => {
  const stageCode = selectedLot.value?.current_stage_code
  if (!stageCode) return null
  return (
    pieceRates.value.find(
      (r) => r.stage_code === stageCode && r.shift === currentShift.value,
    ) ?? null
  )
})

const shiftLabel = computed(() =>
  currentShift.value === 'night' ? t('kiosk.session.shiftNight') : t('kiosk.session.shiftDay'),
)

function setShift(shift: 'day' | 'night') {
  if (activeSession.value || currentShift.value === shift) return
  currentShift.value = shift
  void loadPieceRates()
}

function openDowntimeModal() {
  if (!selectedWorkerId.value) {
    actionError.value = t('kiosk.session.selectEmployee')
    return
  }
  downtimeForm.reason_code = downtimeReasons.value[0]?.code ?? ''
  downtimeForm.notes = ''
  downtimeForm.machine_id = null
  actionError.value = ''
  showDowntime.value = true
}

async function submitDowntime() {
  actionError.value = ''
  if (!downtimeForm.reason_code) {
    actionError.value = t('kiosk.session.selectReason')
    return
  }
  submitting.value = true
  try {
    await openDowntime({
      reason_code: downtimeForm.reason_code,
      machine_id: downtimeForm.machine_id ?? undefined,
      order_id: selectedLot.value?.production_order_id ?? null,
      lot_number: selectedLot.value?.lot_number ?? null,
      start_time: nowApiDateTime(),
      notes: downtimeForm.notes.trim() || undefined,
    })
    showDowntime.value = false
    actionMessage.value = t('kiosk.session.reportDowntime')
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <!-- Worker picker -->
  <div v-if="showWorkerPicker" class="worker-picker">
    <div class="worker-picker__panel">
      <div class="picker-tabs">
        <button :class="['picker-tab', { active: loginMode === 'pick' }]" @click="switchLoginMode('pick')">
          {{ t('kiosk.session.selectWorker') }}
        </button>
        <button :class="['picker-tab', { active: loginMode === 'card' }]" @click="switchLoginMode('card')">
          {{ t('kiosk.session.cardLogin') }}
        </button>
      </div>

      <!-- Pick from list -->
      <template v-if="loginMode === 'pick'">
        <div class="worker-grid">
          <button
            v-for="w in workers"
            :key="w.id"
            type="button"
            class="worker-card"
            @click="selectWorker(w.id)"
          >
            <span class="worker-card__avatar">{{ (w.first_name || '?').charAt(0) }}</span>
            <span class="worker-card__name">{{ workerLabel(w) }}</span>
            <span v-if="w.job_title" class="worker-card__role">{{ w.job_title }}</span>
          </button>
        </div>
      </template>

      <!-- Card / PIN login -->
      <form v-else class="card-login" @submit.prevent="submitWorkerLogin">
        <p class="card-login__title">{{ t('kiosk.session.cardLogin') }}</p>
        <label>{{ t('kiosk.session.cardUid') }}
          <input
            ref="cardInput"
            v-model="loginCard"
            type="text"
            autocomplete="off"
            :placeholder="t('kiosk.session.cardPlaceholder')"
          />
        </label>
        <label>{{ t('kiosk.session.pin') }}
          <input v-model="loginPin" type="password" inputmode="numeric" autocomplete="off" placeholder="••••" />
        </label>
        <div class="pin-pad" role="group" :aria-label="t('kiosk.session.pin')">
          <button v-for="n in 9" :key="n" type="button" class="pin-pad__key" @click="pinAppend(String(n))">{{ n }}</button>
          <button type="button" class="pin-pad__key" @click="pinClear">C</button>
          <button type="button" class="pin-pad__key" @click="pinAppend('0')">0</button>
          <button type="button" class="pin-pad__key" @click="pinBackspace">⌫</button>
        </div>
        <small v-if="loginError" class="error">{{ loginError }}</small>
        <button type="submit" class="btn-primary" :disabled="submitting">
          {{ submitting ? t('kiosk.session.recording') : t('kiosk.session.login') }}
        </button>
      </form>

      <button type="button" class="btn-secondary" @click="showWorkerPicker = false">
        {{ t('kiosk.session.clear') }}
      </button>
    </div>
  </div>

  <!-- Offline / pending sync banner -->
  <div
    v-if="!isOnline || pendingCount > 0"
    class="sync-banner"
    :class="{ 'sync-banner--offline': !isOnline }"
    role="status"
  >
    <span class="sync-banner__icon" aria-hidden="true">⟳</span>
    <span class="sync-banner__text">
      <strong>{{ !isOnline ? t('kiosk.session.offline') : t('kiosk.session.pending') }}</strong>
      <small v-if="pendingCount > 0">{{ t('kiosk.session.pendingCount', { count: pendingCount }) }} — {{ t('kiosk.session.queuedMessage') }}</small>
      <small v-else>{{ t('kiosk.session.queuedMessage') }}</small>
    </span>
    <button
      type="button"
      class="btn-secondary sync-banner__action"
      :disabled="syncing || !isOnline || pendingCount === 0"
      @click="flushQueue"
    >
      {{ syncing ? t('kiosk.session.syncing') : t('kiosk.session.syncNow') }}
    </button>
  </div>

  <div class="page-heading">
    <div>
      <p class="eyebrow">{{ t('kiosk.session.eyebrow') }}</p>
      <h2>{{ t('kiosk.session.title') }}</h2>
      <p>{{ t('kiosk.session.description') }}</p>
    </div>
    <button v-if="selectedWorker" type="button" class="btn-worker" @click="pickWorker">
      <span class="btn-worker__avatar">{{ selectedWorker.first_name.charAt(0) }}</span>
      <span>
        <small>{{ t('kiosk.session.worker') }}</small>
        <strong>{{ workerLabel(selectedWorker) }}</strong>
      </span>
    </button>
    <button v-else type="button" class="btn-primary" @click="pickWorker">
      {{ t('kiosk.session.selectWorker') }}
    </button>
  </div>

  <!-- Loading -->
  <div v-if="loading" class="state-message"><p>{{ t('kiosk.session.loading') }}</p></div>

  <!-- Error -->
  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('kiosk.session.retry') }}</button>
  </div>

  <!-- No orders -->
  <div v-else-if="orders.length === 0" class="state-message">
    <p>{{ t('kiosk.session.noOrders') }}</p>
  </div>

  <template v-else>
    <div class="session-grid">
      <!-- Lot list -->
      <section class="panel">
        <h3>{{ t('kiosk.session.lot') }} — {{ activeLots.length }} {{ t('kiosk.session.activeLots') }}</h3>

        <!-- Scan -->
        <form class="scanner" @submit.prevent="openScannedLot">
          <input
            ref="scanInput"
            v-model="scan"
            type="text"
            autocomplete="off"
            :placeholder="t('kiosk.session.scanOrderOrLot')"
            class="scan-input"
          />
          <button type="submit" class="btn-primary">{{ t('kiosk.session.scan') }}</button>
        </form>
        <small v-if="scanError" class="error">{{ scanError }}</small>

        <div v-if="activeLots.length === 0" class="empty-inline">{{ t('kiosk.session.noLots') }}</div>
        <div v-else class="lot-grid">
          <button
            v-for="lot in activeLots"
            :key="lot.id"
            type="button"
            :class="['lot-card', { selected: selectedLot?.id === lot.id }]"
            @click="selectLot(lot)"
          >
            <span class="lot-card__top">
              <span class="lot-card__order">{{ lot.order_code }}</span>
              <span class="lot-card__qty">{{ lot.current_quantity }} / {{ lot.initial_quantity }}</span>
            </span>
            <span class="lot-card__code">{{ lot.lot_number }}</span>
            <span v-if="lot.product_name" class="lot-card__product">{{ lot.product_name }}</span>
            <span class="lot-card__stage">
              {{ stageName(lot.current_stage_code) }}
              <span v-if="lot.color_variant" class="lot-card__color">{{ lot.color_variant }}</span>
            </span>
          </button>
        </div>
      </section>

    </div>

    <!-- Chosen lot — the entry card opens as a modal with a back button to the
         work-session (lot list) page. -->
    <div v-if="selectedLot && selectedWorkerId" class="work-overlay" @click.self="clearSelection">
      <section class="work-modal" role="dialog" aria-modal="true" :aria-label="t('kiosk.session.selectedLot')">
        <header class="work-modal__header">
          <button type="button" class="btn-back" @click="clearSelection">
            <ArrowLeft :size="18" aria-hidden="true" />
            {{ t('kiosk.session.backToWork') }}
          </button>
          <div class="work-modal__headline">
            <h3 class="work-modal__operation">{{ stageName(selectedLot.current_stage_code) }}</h3>
            <div class="work-modal__order">
              <span v-if="selectedLot.order_code" class="work-modal__mo">{{ selectedLot.order_code }}</span>
              <span v-if="selectedLot.product_name" class="work-modal__product">{{ selectedLot.product_name }}</span>
            </div>
            <p class="selected-meta">
              {{ t('kiosk.session.selectedLot') }}: {{ selectedLot.lot_number }} ·
              {{ selectedLot.current_quantity }} / {{ selectedLot.initial_quantity }}
            </p>
          </div>
        </header>

          <!-- Shift selection: rates differ per shift (day / night). Locked
               while a session is active — the session records its own shift. -->
          <div class="shift-toggle" role="group" :aria-label="t('kiosk.session.shift')">
            <span class="shift-toggle__label">{{ t('kiosk.session.shift') }}</span>
            <button
              v-for="s in ['day', 'night'] as const"
              :key="s"
              type="button"
              class="shift-toggle__btn"
              :class="{ active: currentShift === s }"
              :disabled="!!activeSession"
              @click="setShift(s)"
            >
              <span class="shift-toggle__icon" aria-hidden="true">
                <Sun v-if="s === 'day'" :size="18" />
                <Moon v-else :size="18" />
              </span>
              {{ s === 'day' ? t('kiosk.session.shiftDay') : t('kiosk.session.shiftNight') }}
            </button>
          </div>

          <!-- Step 1: no worker bound yet — prompt to swipe / select -->
          <div v-if="!selectedWorker" class="bind-hint">
            {{ t('kiosk.session.swipeHint') }}
          </div>

          <!-- Output steppers -->
          <div class="stepper-row">
            <div class="stepper stepper--good">
              <span class="stepper__label"><CircleCheck :size="18" aria-hidden="true" /> {{ t('kiosk.session.completed') }}</span>
              <div class="stepper__controls">
                <button type="button" @click="output.completed = Math.max(0, output.completed - 1)">−</button>
                <strong>{{ output.completed }}</strong>
                <button type="button" @click="output.completed += 1">+</button>
              </div>
            </div>
            <div class="stepper stepper--reject">
              <span class="stepper__label"><CircleX :size="18" aria-hidden="true" /> {{ t('kiosk.session.rejected') }}</span>
              <div class="stepper__controls">
                <button type="button" @click="output.rejected = Math.max(0, output.rejected - 1)">−</button>
                <strong>{{ output.rejected }}</strong>
                <button type="button" @click="output.rejected += 1">+</button>
              </div>
            </div>
            <div class="stepper stepper--rework">
              <span class="stepper__label"><RotateCcw :size="18" aria-hidden="true" /> {{ t('kiosk.session.rework') }}</span>
              <div class="stepper__controls">
                <button type="button" @click="output.rework = Math.max(0, output.rework - 1)">−</button>
                <strong>{{ output.rework }}</strong>
                <button type="button" @click="output.rework += 1">+</button>
              </div>
            </div>
          </div>

          <!-- Worker session -->
          <div class="session-bar">
            <div class="session-status">
              <span class="session-status__label">{{ t('kiosk.session.sessionStatus') }}</span>
              <strong v-if="activeSession" :class="`session-${activeSession.status}`">
                {{ sessionStatusLabel(activeSession) }}
              </strong>
              <strong v-else class="session-none">—</strong>
              <span v-if="activeSession && activeSession.total_seconds" class="session-elapsed">
                {{ t('kiosk.session.elapsed', { minutes: elapsedMinutes(activeSession) }) }}
              </span>
            </div>
            <div
              class="session-actions"
              :class="{ 'session-actions--single': !activeSession || activeSession.status === 'finished' }"
            >
              <button
                v-if="!activeSession || activeSession.status === 'finished'"
                type="button"
                class="btn-primary btn-big"
                :disabled="submitting"
                @click="submitStartSession"
              >
                <Play :size="18" aria-hidden="true" />
                {{ submitting ? t('kiosk.session.starting') : t('kiosk.session.startSession') }}
              </button>
              <button
                v-if="activeSession && activeSession.status === 'active'"
                type="button"
                class="btn-secondary btn-big"
                :disabled="submitting"
                @click="submitPause"
              >
                <Pause :size="18" aria-hidden="true" />
                {{ t('kiosk.session.pause') }}
              </button>
              <button
                v-if="activeSession && activeSession.status === 'paused'"
                type="button"
                class="btn-secondary btn-big"
                :disabled="submitting"
                @click="submitResume"
              >
                <Play :size="18" aria-hidden="true" />
                {{ t('kiosk.session.resume') }}
              </button>
              <button
                v-if="activeSession && activeSession.status !== 'finished'"
                type="button"
                class="btn-success btn-big"
                :disabled="submitting"
                @click="submitFinish"
              >
                <CircleCheck :size="18" aria-hidden="true" />
                {{ submitting ? t('kiosk.session.finishing') : t('kiosk.session.finish') }}
              </button>
            </div>
          </div>

          <!-- Live earnings estimate -->
          <div v-if="activeSession && activeSession.status !== 'finished'" class="estimate-bar">
            <span class="estimate-bar__label">{{ t('kiosk.session.liveEstimate') }}</span>
            <strong v-if="estimatedEarnings != null" class="estimate-bar__value">
              {{ estimatedEarnings.toLocaleString() }}
              <small>{{ estimatedCurrency }}</small>
            </strong>
            <span v-else-if="stageRate" class="estimate-bar__hint">
              {{ t('kiosk.session.ratePerPair') }} ({{ shiftLabel }}): {{ stageRate.rate_per_pair }}
            </span>
            <span v-else class="estimate-bar__hint">{{ t('kiosk.session.noRate') }}</span>
          </div>

          <small v-if="actionError" class="error">{{ actionError }}</small>
          <p v-if="actionMessage" class="action-message">{{ actionMessage }}</p>

          <div class="action-row">
            <button
              type="button"
              class="btn-primary btn-big"
              :disabled="submitting || (output.completed <= 0 && output.rejected <= 0 && output.rework <= 0)"
              @click="submitOutput"
            >
              {{ submitting ? t('kiosk.session.recording') : t('kiosk.session.record') }}
            </button>
            <button type="button" class="btn-success btn-big" :disabled="submitting" @click="submitClose">
              {{ t('kiosk.session.closeStage') }}
            </button>
            <button type="button" class="btn-warn btn-big" :disabled="submitting" @click="openDowntimeModal">
              {{ t('kiosk.session.downtime') }}
            </button>
          </div>
      </section>
    </div>

    <!-- Downtime modal -->
    <div v-if="showDowntime" class="modal-overlay" @click.self="showDowntime = false">
      <form class="modal" @submit.prevent="submitDowntime">
        <h3>{{ t('kiosk.session.reportDowntime') }}</h3>
        <label>{{ t('kiosk.session.selectReason') }}
          <select v-model="downtimeForm.reason_code" required>
            <option v-for="r in downtimeReasons" :key="r.code" :value="r.code">{{ reasonLabel(r.code, r.name) }}</option>
          </select>
        </label>
        <label>{{ t('kiosk.session.machine') }}
          <select v-model.number="downtimeForm.machine_id">
            <option :value="null">{{ t('kiosk.session.noMachine') }}</option>
            <option v-for="e in equipment" :key="e.id" :value="e.id">{{ e.name }}</option>
          </select>
          <small v-if="equipment.length === 0" class="error">{{ t('kiosk.session.noMachines') }}</small>
        </label>
        <label>{{ t('kiosk.session.notes') }}
          <input v-model="downtimeForm.notes" type="text" />
        </label>
        <small v-if="actionError" class="error">{{ actionError }}</small>
        <div class="modal-actions">
          <button type="button" class="btn-secondary" @click="showDowntime = false">{{ t('kiosk.state.retry') }}</button>
          <button type="submit" class="btn-primary" :disabled="submitting">
            {{ submitting ? t('kiosk.session.reporting') : t('kiosk.session.report') }}
          </button>
        </div>
      </form>
    </div>
  </template>
</template>

<style scoped>
.page-heading {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  gap: 16px;
}
.eyebrow {
  margin: 0 0 4px;
  color: #65736e;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}
h2 { margin: 0 0 4px; font-size: 22px; font-weight: 700; }
.page-heading p { margin: 0; color: #5f6b6a; font-size: 14px; }

.btn-worker {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px;
  background: #fff;
  border: 1px solid #147a8a;
  border-radius: 10px;
  cursor: pointer;
  text-align: left;
}
.btn-worker:hover { background: #f0f7f8; }
.btn-worker__avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #147a8a;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 16px;
}
.btn-worker small { display: block; color: #65736e; font-size: 11px; text-transform: uppercase; }
.btn-worker strong { display: block; color: #1a2b26; font-size: 14px; }

.state-message { padding: 40px 0; text-align: center; color: #5f6b6a; }
.state-message.error { color: #b33a3a; }
.empty-inline { color: #8b9a95; font-size: 13px; font-style: italic; }
.error { color: #b33a3a; font-size: 12px; }

.bind-hint {
  padding: 10px 14px;
  margin-bottom: 12px;
  background: #eaf4f5;
  border: 1px solid #bfe0d7;
  border-radius: 8px;
  color: #0f6573;
  font-size: 13px;
  font-weight: 600;
}

.shift-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}
.shift-toggle__label {
  font-size: 12px;
  font-weight: 700;
  color: #65736e;
  text-transform: uppercase;
}
.shift-toggle__btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 18px;
  background: #fff;
  color: #65736e;
  border: 1px solid #d4d8d5;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}
.shift-toggle__icon {
  display: inline-grid;
  place-items: center;
}
.shift-toggle__btn.active {
  color: #fff;
  background: #147a8a;
  border: none;
}
.shift-toggle__btn:disabled {
  opacity: 0.65;
  cursor: default;
}

.session-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}
.panel {
  background: #fff;
  border: 1px solid #e2e6e3;
  border-radius: 12px;
  padding: 20px;
}
.panel h3 { margin: 0 0 14px; font-size: 15px; font-weight: 700; color: #1a2b26; }

.scanner { display: flex; gap: 8px; margin-bottom: 8px; }
.scan-input {
  flex: 1;
  padding: 12px 14px;
  border: 1px solid #d4d8d5;
  border-radius: 10px;
  font-size: 15px;
}

.lot-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-top: 12px;
}
.lot-card {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 14px;
  background: #f6f8f7;
  border: 1px solid #e2e6e3;
  border-radius: 10px;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s, background 0.15s;
}
.lot-card:hover { border-color: #147a8a; }
.lot-card.selected { border-color: #147a8a; background: #eaf4f5; }
.lot-card__top {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
}
.lot-card__order {
  overflow: hidden;
  font-size: 12px;
  font-weight: 800;
  color: #147a8a;
  font-family: monospace;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.lot-card__code { font-weight: 800; font-size: 15px; color: #1a2b26; font-family: monospace; }
.lot-card__product {
  font-size: 13px;
  font-weight: 600;
  color: #1a2b26;
}
.lot-card__stage {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  font-size: 12px;
  color: #65736e;
}
.lot-card__qty { font-size: 12px; font-weight: 600; color: #147a8a; }
.lot-card__color {
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 999px;
  color: #0f6573;
  background: #eaf4f5;
  font-size: 11px;
  font-weight: 700;
}

.work-panel .selected-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
}
.work-panel .selected-header h3 { margin: 0; }
.selected-meta { margin: 4px 0 0; font-size: 13px; color: #65736e; }

.stepper-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 16px;
}
.stepper {
  text-align: center;
  padding: 14px;
  background: #f6f8f7;
  border-radius: 10px;
}
.stepper__label {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  color: #65736e;
  text-transform: uppercase;
  margin-bottom: 10px;
}
.stepper__controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}
.stepper__controls button {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  border: 1px solid #d4d8d5;
  background: #fff;
  font-size: 22px;
  font-weight: 700;
  color: #147a8a;
  cursor: pointer;
}
.stepper__controls button:hover { background: #eaf4f5; }
.stepper__controls strong { font-size: 24px; font-weight: 800; color: #1a2b26; min-width: 40px; }

.action-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-top: 12px;
}
.btn-big {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px 12px;
  font-size: 15px;
  border-radius: 12px;
}
.btn-success { background: #1f5c4b; }
.btn-success:hover:not(:disabled) { background: #17493b; }
.btn-warn { background: #b3472d; }
.btn-warn:hover:not(:disabled) { background: #96391f; }
.btn-success, .btn-warn { color: #fff; border: none; font-weight: 700; cursor: pointer; }
.btn-success:disabled, .btn-warn:disabled { opacity: 0.5; cursor: default; }

.action-message {
  padding: 10px 14px;
  background: #dcefe7;
  color: #1f5c4b;
  border-radius: 8px;
  font-size: 13px;
  margin: 8px 0;
}

.session-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  background: #f6f8f7;
  border: 1px solid #e2e6e3;
  border-radius: 10px;
  margin-bottom: 12px;
}
.session-status {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.session-status__label {
  font-size: 11px;
  font-weight: 700;
  color: #65736e;
  text-transform: uppercase;
}
.session-status strong { font-size: 14px; font-weight: 800; }
.session-active { color: #1f5c4b; }
.session-paused { color: #856404; }
.session-finished { color: #65736e; }
.session-none { color: #8b9a95; }
.session-elapsed { font-size: 12px; color: #65736e; }
.session-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}
/* Single action (Start, or a finished session) spans the full row; when a
   session is active/paused, Pause/Resume and Finish share the row. */
.session-actions--single {
  grid-template-columns: 1fr;
}

.btn-primary {
  display: inline-block;
  padding: 10px 20px;
  background: #147a8a;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}
.btn-primary:disabled { opacity: 0.5; cursor: default; }
.btn-primary:hover:not(:disabled) { background: #0f6573; }
.btn-secondary {
  padding: 8px 16px;
  background: #fff;
  color: #147a8a;
  border: 1px solid #147a8a;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}
.btn-secondary:hover { background: #f0f7f8; }

.sync-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  margin-bottom: 16px;
  background: #fdf6e3;
  border: 1px solid #e7d28a;
  border-radius: 10px;
}
.sync-banner--offline {
  background: #fdeeee;
  border-color: #e7b8b8;
}
.sync-banner__icon {
  font-size: 18px;
  color: #8a6d1a;
}
.sync-banner--offline .sync-banner__icon { color: #a12c2c; }
.sync-banner__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  color: #5f5a3a;
}
.sync-banner__text strong { font-size: 13px; font-weight: 800; }
.sync-banner__text small { font-size: 12px; }
.sync-banner__action { flex-shrink: 0; }

.estimate-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  background: #eef7f5;
  border: 1px solid #bfe0d7;
  border-radius: 10px;
  margin-top: 12px;
}
.estimate-bar__label {
  font-size: 12px;
  font-weight: 700;
  color: #1f5c4b;
  text-transform: uppercase;
}
.estimate-bar__value {
  font-size: 18px;
  font-weight: 800;
  color: #1f5c4b;
}
.estimate-bar__value small { font-size: 12px; font-weight: 600; }
.estimate-bar__hint { font-size: 12px; color: #65736e; }

.picker-tabs {
  display: flex;
  gap: 0;
  margin-bottom: 16px;
  border-bottom: 2px solid #e2e6e3;
}
.picker-tab {
  padding: 10px 20px;
  font-size: 13px;
  font-weight: 700;
  color: #65736e;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  cursor: pointer;
}
.picker-tab.active { color: #147a8a; border-bottom-color: #147a8a; }
.card-login {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
}
.card-login label {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  color: #65736e;
}
.card-login input {
  padding: 12px 14px;
  border: 1px solid #d4d8d5;
  border-radius: 10px;
  font-size: 16px;
  background: #fff;
}

.pin-pad {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}
.pin-pad__key {
  min-height: 52px;
  font-size: 20px;
  font-weight: 700;
  color: #1a2b26;
  background: #f6f8f7;
  border: 1px solid #d4d8d5;
  border-radius: 10px;
  cursor: pointer;
}
.pin-pad__key:hover {
  border-color: #147a8a;
  background: #eaf4f5;
}

.worker-picker {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 20, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 60;
}
.worker-picker__panel {
  width: min(640px, 92vw);
  max-height: 86vh;
  overflow-y: auto;
  background: #fff;
  border-radius: 14px;
  padding: 28px;
}
.worker-picker__panel h2 { margin: 0 0 16px; font-size: 18px; }
.worker-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 16px;
}
.worker-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  background: #f6f8f7;
  border: 1px solid #e2e6e3;
  border-radius: 10px;
  cursor: pointer;
  text-align: left;
}
.worker-card:hover { border-color: #147a8a; background: #eaf4f5; }
.worker-card__avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #147a8a;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 18px;
}
.worker-card__name { font-weight: 700; color: #1a2b26; }
.worker-card__role { display: block; font-size: 12px; color: #65736e; }

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 20, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
}
.modal {
  width: 380px;
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.modal h3 { margin: 0; font-size: 16px; font-weight: 700; }
.modal label {
  display: flex;
  flex-direction: column;
  font-size: 12px;
  font-weight: 600;
  color: #65736e;
  gap: 4px;
}
.modal input, .modal select {
  padding: 10px 12px;
  border: 1px solid #d4d8d5;
  border-radius: 8px;
  font-size: 14px;
  background: #fff;
}
.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
}

/* Chosen-lot entry card — full modal with a back button to the lot list. */
.work-overlay {
  position: fixed;
  inset: 0;
  z-index: 45;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgb(15 23 20 / 55%);
}
.work-modal {
  width: min(760px, 100%);
  max-height: calc(100dvh - 32px);
  overflow-y: auto;
  background: #fff;
  border-radius: 14px;
  padding: 24px;
  box-shadow: 0 20px 60px rgb(6 38 84 / 18%);
}
.work-modal__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 16px;
}
.work-modal__headline {
  min-width: 0;
  text-align: right;
}
.work-modal__operation {
  margin: 0 0 2px;
  font-size: 26px;
  font-weight: 800;
  line-height: 1.15;
  color: #1a2b26;
}
.work-modal__order {
  display: flex;
  align-items: baseline;
  justify-content: flex-end;
  gap: 10px;
}
.work-modal__mo {
  font-size: 15px;
  font-weight: 800;
  color: #147a8a;
  font-family: monospace;
}
.work-modal__product {
  font-size: 14px;
  font-weight: 600;
  color: #5f6b6a;
}
.btn-back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
  padding: 10px 16px;
  border: 0;
  border-radius: 10px;
  color: #fff;
  background: #147a8a;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}
.btn-back:hover { background: #0f6573; }

/* Touch-kiosk: portrait 15" panel or compact width -------------------------
   Big finger targets, full-width controls, panels stacked vertically. */
@media (orientation: portrait), (max-width: 1180px) {
  /* Heading compacts so the work area starts higher; the worker button
     becomes a full-width tap target. */
  .page-heading {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    margin-bottom: 12px;
  }
  .page-heading h2 { font-size: 20px; }
  .btn-worker { padding: 10px 16px; }
  .btn-worker__avatar { width: 44px; height: 44px; font-size: 20px; }
  .btn-worker strong { font-size: 15px; }

  .session-grid { grid-template-columns: 1fr; gap: 12px; }
  .panel { padding: 16px; }
  .panel h3 { font-size: 15px; }

  .scanner { gap: 10px; }
  .scan-input { min-height: 52px; font-size: 16px; }
  .scanner .btn-primary { min-height: 52px; }
  .error { font-size: 13px; }

  .lot-grid { grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 10px; }
  .lot-card { padding: 16px; min-height: 84px; }
  .lot-card__code { font-size: 16px; }
  .lot-card__product { font-size: 15px; }
  .lot-card__stage,
  .lot-card__qty { font-size: 14px; }

  .selected-header { align-items: center; }
  .selected-meta { font-size: 14px; }

  .shift-toggle { display: flex; margin-bottom: 14px; }
  .shift-toggle__label { font-size: 13px; }
  .shift-toggle__btn {
    flex: 1;
    min-height: 52px;
    font-size: 16px;
    border-radius: 10px;
  }

  /* Three big steppers across the full-width work panel. */
  .stepper-row { grid-template-columns: repeat(3, 1fr); gap: 12px; }
  .stepper { padding: 16px 12px; }
  .stepper__label { font-size: 13px; margin-bottom: 12px; }
  .stepper__controls { gap: 12px; }
  .stepper__controls button {
    width: 64px;
    height: 64px;
    border-radius: 14px;
    font-size: 30px;
  }
  .stepper__controls strong { font-size: 30px; min-width: 52px; }

  .btn-big {
    min-height: 64px;
    padding: 16px 12px;
    font-size: 17px;
    border-radius: 14px;
  }
  .action-row { grid-template-columns: repeat(3, 1fr); gap: 10px; }
  .btn-primary,
  .btn-secondary { min-height: 48px; }
  .action-message { font-size: 15px; }

  .session-bar { flex-direction: column; align-items: stretch; gap: 10px; }
  .session-status { flex-direction: row; align-items: center; gap: 10px; }
  .session-status strong { font-size: 16px; }
  .session-elapsed { font-size: 14px; }
  .session-actions { gap: 10px; }

  .estimate-bar { flex-direction: column; align-items: stretch; text-align: left; }
  .estimate-bar__value { font-size: 22px; }

  .sync-banner { flex-direction: column; align-items: stretch; gap: 8px; }

  /* Worker picker — auto-fill columns so staff fit without scrolling. */
  .worker-picker__panel { width: min(600px, 94vw); padding: 22px; }
  .worker-grid { grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 10px; }
  .worker-card { padding: 14px; min-height: 76px; }
  .worker-card__avatar { width: 48px; height: 48px; font-size: 20px; }
  .worker-card__name { font-size: 15px; }
  .worker-card__role { font-size: 13px; }

  .picker-tab { padding: 12px 16px; font-size: 14px; }
  .card-login { gap: 14px; }
  .card-login label { font-size: 13px; }
  .card-login input { min-height: 52px; font-size: 18px; }
  .card-login .btn-primary { min-height: 56px; font-size: 16px; }
  .pin-pad { gap: 10px; }
  .pin-pad__key { min-height: 64px; font-size: 24px; border-radius: 12px; }

  .modal { width: min(460px, 92vw); }
  .modal input,
  .modal select { min-height: 52px; font-size: 16px; }
  .modal-actions button { min-height: 52px; font-size: 15px; }

  .work-overlay { align-items: stretch; padding: 10px; }
  .work-modal {
    width: 100%;
    max-height: calc(100dvh - 20px);
    border-radius: 12px;
    padding: 16px;
  }
  .work-modal__header {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
  .btn-back {
    min-height: 52px;
    font-size: 16px;
    border-radius: 10px;
  }
}

/* Genuinely narrow screens: collapse the remaining multi-column grids. */
@media (max-width: 600px) {
  .lot-grid { grid-template-columns: 1fr; }
  .stepper-row { grid-template-columns: 1fr; }
  .action-row { grid-template-columns: 1fr; }
}
</style>
